import { useState } from "react";
import { Reveal } from "../components/Reveal";
import { CONTACT } from "../data/content";
import { Phone, Mail, MapPin, Clock, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

export const ContactCTA = ({ hideForm = false }) => {
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [ts] = useState(() => Math.floor(Date.now() / 1000));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch("kontakt.php", { method: "POST", body: new FormData(e.currentTarget) });
      const data = await res.json();
      setStatus(data.ok ? "sent" : "error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section data-testid="contact-section" className="py-20 lg:py-32 bg-white border-t border-[#E5E0D8]" id="kontakt">
      <div className="container-blum">
        <div className={hideForm ? "max-w-3xl mx-auto text-center" : "grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-stretch"}>
          {/* Left – invitation */}
          <div className="flex flex-col">
            <Reveal><p className="overline">Kontakt</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">
                Ihr Traumboden beginnt mit einem Gespräch.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-[#6B6B73] text-lg leading-relaxed mt-5">
                Schreiben Sie uns Ihren Terminwunsch – oder rufen Sie einfach an.
                Wir nehmen uns Zeit für Sie.
              </p>
            </Reveal>

            <Reveal delay={0.15} className="mt-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-10">
                <a href={CONTACT.phoneHref} className="flex items-center gap-4 p-5 bg-[#F9F8F6] border border-[#E5E0D8] lift">
                  <Phone className="w-6 h-6 text-[#D32F2F]" />
                  <div><p className="text-xs text-[#6B6B73] uppercase tracking-wide">Telefon</p><p className="font-display font-semibold text-[#2B2B30]">{CONTACT.phone}</p></div>
                </a>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 p-5 bg-[#F9F8F6] border border-[#E5E0D8] lift">
                  <Mail className="w-6 h-6 text-[#D32F2F]" />
                  <div><p className="text-xs text-[#6B6B73] uppercase tracking-wide">E-Mail</p><p className="font-display font-semibold text-[#2B2B30] text-sm">{CONTACT.email}</p></div>
                </a>
                <div className="flex items-center gap-4 p-5 bg-[#F9F8F6] border border-[#E5E0D8]">
                  <MapPin className="w-6 h-6 text-[#D32F2F]" />
                  <div><p className="text-xs text-[#6B6B73] uppercase tracking-wide">Adresse</p><p className="font-display font-semibold text-[#2B2B30] text-sm">{CONTACT.street}, {CONTACT.city}</p></div>
                </div>
                <div className="flex items-center gap-4 p-5 bg-[#F9F8F6] border border-[#E5E0D8]">
                  <Clock className="w-6 h-6 text-[#D32F2F]" />
                  <div><p className="text-xs text-[#6B6B73] uppercase tracking-wide">Mo – Fr</p><p className="font-display font-semibold text-[#2B2B30] text-sm">08–17 Uhr</p></div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right – form / success */}
          {!hideForm && (
          <Reveal delay={0.1}>
            {status === "sent" ? (
              <div data-testid="contact-success" className="bg-[#2B2B30] p-8 lg:p-10 h-full flex flex-col items-center justify-center text-center">
                <span className="w-14 h-14 flex items-center justify-center bg-[#D32F2F]">
                  <CheckCircle2 className="w-7 h-7 text-white" />
                </span>
                <h3 className="font-display text-2xl font-bold text-white mt-6">Vielen Dank!</h3>
                <p className="text-white/70 mt-3 leading-relaxed max-w-sm">
                  Ihre Nachricht wurde erfolgreich versendet. Wir melden uns zeitnah bei Ihnen.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  data-testid="contact-reset"
                  className="btn-base border border-white/30 text-white hover:bg-white/10 transition-colors mt-8"
                >
                  Neue Nachricht schreiben
                </button>
              </div>
            ) : (
            <form onSubmit={onSubmit} data-testid="contact-form" className="relative bg-[#2B2B30] p-8 lg:p-10 h-full">
              <h3 className="font-display text-2xl font-bold text-white mb-8">Termin anfragen</h3>

              {/* Anti-Bot: Zeitstempel + Honeypot (für Menschen unsichtbar) */}
              <input type="hidden" name="ts" value={ts} />
              <input
                type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                className="absolute -left-[9999px] top-0 h-0 w-0 opacity-0" defaultValue=""
              />

              <div className="space-y-5">
                <div>
                  <label className="block text-white/70 text-sm mb-2 font-display">Name</label>
                  <input
                    name="name" required
                    data-testid="contact-name"
                    className="w-full bg-white/5 border border-white/15 text-white px-4 py-3 outline-none focus:border-[#D32F2F] transition-colors placeholder:text-white/30"
                    placeholder="Ihr Name"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2 font-display">Telefon / E-Mail</label>
                  <input
                    name="phone" required
                    data-testid="contact-phone"
                    className="w-full bg-white/5 border border-white/15 text-white px-4 py-3 outline-none focus:border-[#D32F2F] transition-colors placeholder:text-white/30"
                    placeholder="So erreichen wir Sie"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2 font-display">Ihr Anliegen</label>
                  <textarea
                    name="message" rows={4} required
                    data-testid="contact-message"
                    className="w-full bg-white/5 border border-white/15 text-white px-4 py-3 outline-none focus:border-[#D32F2F] transition-colors resize-none placeholder:text-white/30"
                    placeholder="Welches Projekt planen Sie?"
                  />
                </div>
              </div>

              <button type="submit" disabled={status === "sending"} data-testid="contact-submit" className="btn-base btn-red w-full mt-8 disabled:opacity-60">
                {status === "sending" ? (
                  <>Wird gesendet … <Loader2 className="w-4 h-4 animate-spin" /></>
                ) : (
                  <>Anfrage senden <ArrowRight className="w-4 h-4" /></>
                )}
              </button>

              {status === "error" && (
                <p data-testid="contact-error" className="text-[#FF9E99] text-sm mt-5 leading-relaxed">
                  Die Nachricht konnte leider nicht gesendet werden. Bitte versuchen Sie es erneut –
                  oder rufen Sie uns direkt an: {CONTACT.phone}
                </p>
              )}
            </form>
            )}
          </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};
